"use client";

import { AnimatePresence, motion } from "motion/react";

import { type ReactNode, forwardRef, useMemo } from "react";

import dynamic from "next/dynamic";

import { cx } from "@/lib/tailwind-utils";

import { AddStepButton } from "./add-step-button";
import { useCarePlanContext } from "./care-plan";
import { DateConnector } from "./date-connector";
import { DecisionStep } from "./decision/decision-step";
import { useCarePlanPrint } from "./print/care-plan-print-context";
import { StepCard } from "./step-card";
import { StepCardPopover } from "./step-card-popover";
import { type CarePlanStep, StepType, isLinkedMessage } from "./types";

const StepListItem = forwardRef<
  HTMLDivElement,
  { children: ReactNode; className: string }
>(function StepListItem({ children, className }, ref) {
  const printing = useCarePlanPrint();
  if (printing)
    return (
      <div ref={ref} className={className}>
        {children}
      </div>
    );
  return (
    <motion.div
      ref={ref}
      layout
      initial={{ opacity: 0, scale: 0.95, y: -10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: -10 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
});

const DndStepList = dynamic(
  () => import("./dnd-step-list").then((m) => m.DndStepList),
  { ssr: false },
);

export function StepList() {
  const {
    state: { visibleSteps },
    config: { orientation, detailMode, readOnly },
  } = useCarePlanContext();

  const timelineSteps = useMemo(
    () => visibleSteps.filter((s) => !isLinkedMessage(s)),
    [visibleSteps],
  );

  if (!readOnly) return <DndStepList />;

  const usePopover = detailMode === "popover";
  const isHorizontal = orientation === "horizontal";

  const stepCard = (step: CarePlanStep) =>
    usePopover ? (
      <StepCardPopover stepId={step.id}>
        <StepCard step={step} />
      </StepCardPopover>
    ) : (
      <StepCard step={step} />
    );

  return (
    <div
      className={cx(
        "flex gap-2",
        isHorizontal ? "flex-row items-center" : "flex-col items-center",
      )}
    >
      <AnimatePresence initial={false} mode="popLayout">
        {timelineSteps.map((step, i) => (
          <StepListItem
            key={step.id}
            className={cx(
              "flex",
              isHorizontal ? "flex-row items-center" : "flex-col items-center",
            )}
          >
            {step.type === StepType.DECISION ? (
              <DecisionStep.Root step={step}>
                <div
                  className={cx(
                    "flex",
                    isHorizontal
                      ? "flex-row items-center"
                      : "flex-col items-center",
                  )}
                >
                  {stepCard(step)}
                  <DecisionStep.BranchDisplay>
                    {[...(step.branches ?? [])]
                      .sort((a, b) => a.position - b.position)
                      .map((branch) => (
                        <DecisionStep.Branch key={branch.id} branch={branch}>
                          <DecisionStep.BranchLabel />
                          <DecisionStep.BranchStepList />
                        </DecisionStep.Branch>
                      ))}
                  </DecisionStep.BranchDisplay>
                </div>
              </DecisionStep.Root>
            ) : (
              stepCard(step)
            )}
            {i < timelineSteps.length - 1 && (
              <DateConnector
                minutesGap={timelineSteps[i + 1].minutesAfterPrevious}
              />
            )}
          </StepListItem>
        ))}
      </AnimatePresence>
      <AddStepButton />
    </div>
  );
}
